/**
 * Fristen (docs/17-getraenke.md, Fristen; use case S5): one deadline with two roles. Ripeness is the
 * drinking window of a wine, in years, per article; expiry is the best-before date (MHD) of a lot,
 * given on receipt. Computed from the state each time, against today, so list, hint and filter say
 * the same.
 */
import type { State } from "./ledger.ts";
import type { Id, Millis } from "./model.ts";

/** Ripeness by the drinking window: too young, ready, in its last year, past its peak. */
export type Ripeness = "young" | "ready" | "last" | "past";

/** Expiry of a lot: within the warning time, or past the date. */
export type Expiry = "soon" | "expired";

/** Days before the best-before date a lot shows as „bald“. */
export const SOON_DAYS = 28;

const year = (v: unknown) => (typeof v === "number" && Number.isInteger(v) ? v : null);

/** Where a drink stands in its drinking window in a year; null without a window. */
export function ripeness(f: Record<string, unknown>, inYear: number): Ripeness | null {
  const from = year(f.drinkFrom);
  const to = year(f.drinkTo);
  if (from === null && to === null) return null;
  if (from !== null && inYear < from) return "young";
  if (to !== null && inYear > to) return "past";
  if (to !== null && inYear === to) return "last";
  return "ready";
}

/** A best-before date (yyyy-mm-dd) against a time; null when it is far or not a date. */
export function expiry(bestBefore: string | undefined, now: Millis, soonDays = SOON_DAYS): Expiry | null {
  if (!bestBefore || !/^\d{4}-\d{2}-\d{2}/.test(bestBefore)) return null;
  const end = Date.parse(`${bestBefore.slice(0, 10)}T23:59:59Z`);
  if (!Number.isFinite(end)) return null;
  if (end < now) return "expired";
  return end - now <= soonDays * 86_400_000 ? "soon" : null;
}

export type Deadline =
  | { role: "ripeness"; article: Id; state: Ripeness; from: number | null; to: number | null; qty: number }
  | { role: "expiry"; article: Id; state: Expiry; date: string; lot: Id; lager: Id; location: Id; qty: number };

const URGENCY: Record<string, number> = { expired: 0, past: 1, soon: 2, last: 3, ready: 4, young: 5 };

/**
 * The deadlines of everything in stock, most urgent first: expired, past its peak, soon, last year,
 * ready, too young. `fields` gives an article's master data.
 */
export function deadlines(state: State, fields: (article: Id) => Record<string, unknown> | undefined, now: Millis, soonDays = SOON_DAYS): Deadline[] {
  const out: Deadline[] = [];
  const stock = new Map<Id, number>();
  for (const acc of state.accounts.values()) {
    if (acc.qty <= 0) continue;
    stock.set(acc.article, (stock.get(acc.article) ?? 0) + acc.qty);
    if (!acc.lot) continue;
    const lot = state.lots.get(acc.lot);
    const state_ = expiry(lot?.bestBefore, now, soonDays);
    if (lot && state_) out.push({ role: "expiry", article: acc.article, state: state_, date: lot.bestBefore!.slice(0, 10), lot: lot.id, lager: acc.lager, location: acc.location, qty: acc.qty });
  }
  const inYear = new Date(now).getUTCFullYear();
  for (const [article, qty] of stock) {
    const f = fields(article);
    const r = f ? ripeness(f, inYear) : null;
    if (f && r) out.push({ role: "ripeness", article, state: r, from: year(f.drinkFrom), to: year(f.drinkTo), qty });
  }
  const when = (d: Deadline) => (d.role === "expiry" ? d.date : String(d.to ?? d.from ?? ""));
  return out.sort((a, b) => URGENCY[a.state]! - URGENCY[b.state]! || when(a).localeCompare(when(b)) || a.article.localeCompare(b.article));
}

/**
 * What to tell someone taking a drink out (never a block): „Noch zu jung, trinkreif ab 2031“,
 * „Über dem Höhepunkt seit 2040“, „MHD abgelaufen am 03.09.2026“ for the oldest lot at the place.
 */
export function takeHint(state: State, f: Record<string, unknown>, article: Id, location: Id, now: Millis): string | null {
  const stack = state.stacks.get(`${article}|${location}`);
  const oldest = stack?.accounts.slice(stack.head).find((a) => a.qty > 0);
  const lot = oldest ? state.lots.get(oldest.lot) : undefined;
  const e = expiry(lot?.bestBefore, now);
  const date = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
  if (e === "expired") return `MHD abgelaufen am ${date(lot!.bestBefore!)}`;
  const r = ripeness(f, new Date(now).getUTCFullYear());
  if (r === "young") return `Noch zu jung, trinkreif ab ${f.drinkFrom}`;
  if (r === "past") return `Über dem Höhepunkt seit ${Number(f.drinkTo) + 1}`;
  if (e === "soon") return `MHD bald: ${date(lot!.bestBefore!)}`;
  return null;
}
